import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Dog, Cat, Users, Heart } from "lucide-react";
import type { ShelterUpdateWithOriginal } from "@/hooks/useShelterUpdates";

interface UpdateDiffViewProps {
  update: ShelterUpdateWithOriginal;
}

interface FieldDiff {
  label: string;
  oldValue: string | null | undefined;
  newValue: string | null | undefined;
  type: "text" | "boolean" | "longtext";
}

const formatBoolean = (value: boolean | null | undefined): string => {
  if (value === null || value === undefined) return "Brak informacji";
  return value ? "Tak" : "Nie";
};

const formatText = (value: string | null | undefined): string => {
  if (value === null || value === undefined || value === "") return "—";
  return value;
};

const hasChanged = (oldVal: any, newVal: any): boolean => {
  // Treat null, undefined, and empty string as equivalent
  const normalizeValue = (v: any) => {
    if (v === null || v === undefined || v === "") return null;
    return v;
  };
  return normalizeValue(oldVal) !== normalizeValue(newVal);
};

export const UpdateDiffView = ({ update }: UpdateDiffViewProps) => {
  const original = update.originalShelter;
  
  // If no original shelter (new shelter submission), show all fields as new
  const isNewShelter = !original;

  const fields: FieldDiff[] = [
    {
      label: "Nazwa",
      oldValue: original?.name,
      newValue: update.shelter_name,
      type: "text",
    },
    {
      label: "Miasto",
      oldValue: original?.city,
      newValue: update.city,
      type: "text",
    },
    {
      label: "Województwo",
      oldValue: original?.region,
      newValue: update.region,
      type: "text",
    },
    {
      label: "Adres",
      oldValue: original?.address,
      newValue: update.address,
      type: "text",
    },
    {
      label: "Telefon",
      oldValue: original?.phone,
      newValue: update.phone,
      type: "text",
    },
    {
      label: "Email",
      oldValue: original?.email,
      newValue: update.email,
      type: "text",
    },
    {
      label: "Strona www",
      oldValue: original?.website,
      newValue: update.website,
      type: "text",
    },
    {
      label: "Opis",
      oldValue: original?.description,
      newValue: update.description,
      type: "longtext",
    },
    {
      label: "Aktualne potrzeby",
      oldValue: original?.current_needs,
      newValue: update.current_needs,
      type: "longtext",
    },
    {
      label: "Info o wolontariacie",
      oldValue: original?.volunteer_info,
      newValue: update.volunteer_info,
      type: "longtext",
    },
  ];

  const booleanFields: { label: string; icon: any; oldValue: boolean | null | undefined; newValue: boolean | null | undefined }[] = [
    {
      label: "Pilne potrzeby",
      icon: AlertTriangle,
      oldValue: original?.urgent_needs,
      newValue: update.urgent_needs,
    },
    {
      label: "Spacery z psami",
      icon: Heart,
      oldValue: original?.dog_walking_available,
      newValue: update.dog_walking_available,
    },
    {
      label: "Wolontariat",
      icon: Users,
      oldValue: original?.volunteering_available,
      newValue: update.volunteering_available,
    },
    {
      label: "Psy do adopcji",
      icon: Dog,
      oldValue: original?.dogs_available,
      newValue: update.dogs_available,
    },
    {
      label: "Koty do adopcji",
      icon: Cat,
      oldValue: original?.cats_available,
      newValue: update.cats_available,
    },
  ];

  // Filter to show only changed fields (or all if new shelter)
  const changedFields = isNewShelter 
    ? fields.filter(f => f.newValue) 
    : fields.filter(f => hasChanged(f.oldValue, f.newValue));
  
  const changedBooleanFields = isNewShelter 
    ? booleanFields 
    : booleanFields.filter(f => hasChanged(f.oldValue, f.newValue));

  const totalChanges = changedFields.length + changedBooleanFields.length;

  if (totalChanges === 0 && !isNewShelter) {
    return (
      <div className="text-sm text-muted-foreground italic">
        Brak zmian w polach (możliwe że tylko lokalizacja została zmieniona)
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Summary badge */}
      <div className="flex items-center gap-2">
        <Badge variant={isNewShelter ? "default" : "secondary"}>
          {isNewShelter ? "Nowe schronisko" : `${totalChanges} zmian`}
        </Badge>
      </div>

      {/* Changed text fields */}
      {changedFields.length > 0 && (
        <div className="space-y-2">
          {changedFields.map((field) => (
            <div key={field.label} className="text-sm">
              <span className="font-medium text-foreground">{field.label}:</span>
              {isNewShelter ? (
                <div className="mt-1 pl-3 border-l-2 border-primary">
                  <span className={field.type === "longtext" ? "whitespace-pre-wrap" : ""}>
                    {formatText(field.newValue)}
                  </span>
                </div>
              ) : (
                <div className="mt-1 space-y-1">
                  {field.oldValue && (
                    <div className="pl-3 border-l-2 border-destructive/50 bg-destructive/5 rounded-r py-1">
                      <span className="text-xs text-muted-foreground mr-1">Było:</span>
                      <span className={`text-muted-foreground line-through ${field.type === "longtext" ? "whitespace-pre-wrap block mt-1" : ""}`}>
                        {formatText(field.oldValue)}
                      </span>
                    </div>
                  )}
                  <div className="pl-3 border-l-2 border-primary bg-primary/5 rounded-r py-1">
                    <span className="text-xs text-muted-foreground mr-1">Teraz:</span>
                    <span className={`text-foreground font-medium ${field.type === "longtext" ? "whitespace-pre-wrap block mt-1" : ""}`}>
                      {formatText(field.newValue)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Changed boolean fields */}
      {changedBooleanFields.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {changedBooleanFields.map((field) => {
            const Icon = field.icon;
            const isNowTrue = field.newValue === true;
            const isNowNull = field.newValue === null || field.newValue === undefined;
            
            return (
              <div 
                key={field.label}
                className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border ${
                  isNowTrue 
                    ? "bg-primary/10 border-primary/30 text-primary" 
                    : isNowNull
                    ? "bg-accent border-accent/30 text-accent-foreground"
                    : "bg-muted border-border text-muted-foreground"
                }`}
              >
                <Icon className="w-3 h-3" />
                <span>{field.label}:</span>
                {!isNewShelter && (
                  <span className="line-through opacity-50">
                    {formatBoolean(field.oldValue)}
                  </span>
                )}
                {!isNewShelter && <span>→</span>}
                <span className="font-medium">{formatBoolean(field.newValue)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
